import { useMemo } from 'react';
import { crc32 } from 'crc';
import { IRestaurant, MenuModeEnum, OrderModeEnum } from '../repository/vendor';
import { IConfig, IMenuPageMode } from '../repository/config';
import { getQsrRouteParam, IURLTopology, IURLVendor, QSRRouterModeEnum, useQlubRouter } from '../hook/router';
import { useConfigContext } from '../context/config';
import { isIOS } from './k_user_agent';
import { useVendor } from '../context/vendor';
import { isSameOrigin } from './k_url';

export const visitMenuStorage = 'qlub.digital_menu.visited';
export const viewMenuEvent = 'view_menu';
export const infoBoxClickEvent = 'tapOnInformingBox';

export const menuPaths = {
    menuWithPayment: '/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/menu_with_payment',
    menuWithoutPayment: '/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/menu_without_payment',
    legacyMenu: '/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/legacy_menu',
};

interface IMenuPathPayload {
    vendor?: IRestaurant;
    countryConfig?: IConfig;
}

/**
 * @description
 * Returns the menu path based on the vendor and country config
 * @param {IMenuPathPayload} payload
 * @returns {string}
 *
 * */
export const getMenuPath = (payload: IMenuPathPayload) => {
    const { vendor, countryConfig } = payload;

    if (!vendor || !countryConfig) {
        return '';
    }

    // handle common cases
    const setMenuPath = (menuMode: IMenuPageMode, isOnlyMenuVendor?: boolean) => {
        if (menuMode === IMenuPageMode.New) {
            if (isOnlyMenuVendor) {
                return menuPaths.menuWithoutPayment;
            }

            return menuPaths.menuWithPayment;
        }

        return menuPaths.legacyMenu;
    };

    // handle vendor specific cases
    if (vendor?.config?.menuPageMode) {
        return setMenuPath(vendor.config.menuPageMode, vendor.config.onlyMenuVendor);
    }

    // handle country specific cases
    if (countryConfig?.menuPageMode) {
        return setMenuPath(countryConfig.menuPageMode, vendor.config.onlyMenuVendor || false);
    }

    // default case
    return menuPaths.legacyMenu;
};

export const linkHardRefreshHandler = (link: any) => (e: any) => {
    if (link?.refresh) {
        e.preventDefault();
        window.location.href = link.pathname;
    }
};

const getMenuLink = (url: IURLTopology, lang: string | null | undefined, source?: string) => {
    const { getRouterParam } = useQlubRouter();
    const { config: countryConfig } = useConfigContext();

    const iOS = useMemo(() => isIOS(), []);
    const { vendor, qsrSingleMenuId } = useVendor();

    const gotoQsr = () => {
        const qsrParam = { qsr: getQsrRouteParam(vendor) };
        if (qsrSingleMenuId) {
            return getRouterParam(
                `/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/qsr/[menu]`,
                {
                    menu: qsrSingleMenuId,
                    source,
                    ...qsrParam,
                },
                ['qsrPayment'],
            );
        }
        return getRouterParam(
            `/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/qsr`,
            {
                source,
                ...qsrParam,
            },
            ['qsrPayment'],
        );
    };

    const getDefault = () => {
        if (url.qsr === QSRRouterModeEnum.True) {
            return gotoQsr();
        }

        return getRouterParam(getMenuPath({ vendor, countryConfig: countryConfig || undefined }), { source });
    };

    if (!vendor) {
        return getDefault();
    }

    if ([OrderModeEnum.QSR, OrderModeEnum.Mixed].includes(vendor?.orderMode || OrderModeEnum.Default)) {
        return gotoQsr();
    }

    if (
        vendor?.orderMode === OrderModeEnum.DigitalMenu &&
        !(
            vendor?.orderConfig?.skipOnMenuTableUrl &&
            vendor.config?.menuMode === MenuModeEnum.TableUrl &&
            vendor.metaObject?.menuUrl
        )
    ) {
        return gotoQsr();
    }

    switch (vendor.config?.menuMode) {
        case MenuModeEnum.PdfMenuIOSRedirect: {
            if (vendor.externalMenuUrl) {
                if (iOS) {
                    return vendor.externalMenuUrl;
                }
            }
            return getDefault();
        }
        case MenuModeEnum.PDFMenu:
        case MenuModeEnum.ExternalUrl:
        case MenuModeEnum.MultiMenu:
            return getDefault();
        case MenuModeEnum.ExternalUrlSameTab:
            if (vendor.config?.externalMenuUrl || vendor.externalMenuUrl) {
                return vendor.config.externalMenuUrl || vendor.externalMenuUrl;
            }
            return getDefault();
        case MenuModeEnum.TableUrl:
            try {
                if (vendor.metaObject?.menuUrl) {
                    if (isSameOrigin(vendor.metaObject.menuUrl)) {
                        return { pathname: vendor.metaObject.menuUrl, refresh: true };
                    }
                    return vendor.metaObject.menuUrl;
                }

                const tableMap: Array<{
                    key: IURLTopology;
                    value: string;
                }> = JSON.parse(vendor.config?.tableUrl || '');

                if (!tableMap) {
                    return getDefault();
                }
                const table = tableMap.find(
                    (o) => o.key.id === url.id && (o.key.f1 || '_') === url.f1 && (o.key.f2 || '_') === url.f2,
                );
                if (!table || !table.value) {
                    return getDefault();
                }
                return table.value;
            } catch {
                return getDefault();
            }
        default:
            if (vendor.config?.externalMenuSameTab && vendor.config?.externalMenuUrl) {
                return vendor.config.externalMenuUrl;
            }
            return getDefault();
    }
};

const calculateVendorHash = ({ cc, slug }: IURLVendor) => {
    return String(crc32(`${cc}:^&6u3w4f:${slug}`));
};

export const getTableLessMenuLink = ({ cc, slug }: IURLVendor, orderMode: OrderModeEnum | null, query?: any) => {
    if (![OrderModeEnum.DigitalMenu, OrderModeEnum.QSR].includes(orderMode as OrderModeEnum)) {
        return {
            pathname: `/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/menu`,
            query: {
                cc,
                slug,
                id: '_',
                f1: '_',
                f2: '_',
                hash: '_',
                ...(query || {}),
            },
        };
    }

    const hash = calculateVendorHash({ cc, slug });
    return {
        pathname: `/qr/[cc]/[slug]/[id]/[f1]/[f2]/[hash]/qsr`,
        query: {
            cc,
            slug,
            id: '_',
            f1: '_',
            f2: '_',
            hash,
            qsr: OrderModeEnum.DigitalMenu,
            ...(query || {}),
        },
    };
};

export default getMenuLink;
